import type { ObservabilityCollectionComponent, ObservabilityRetentionTableStatus } from '@scatterin/api-types';
import { Activity, CircleHelp, Database, RadioTower, TriangleAlert } from 'lucide-react';

import { DataPanel } from '@/components/dataPanel';
import { StateView } from '@/components/stateViews';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableShell } from '@/components/ui/table';
import { useEnvironmentHealthQuery } from '@/hooks/useObservabilityQueries';
import { useRouteContext } from '@/hooks/useRouteContext';
import { formatBytes, formatInteger, formatRelativeTime, formatTime } from '@/lib/format';

const infrastructureComponentKeys = new Set([
  'database',
  'ingestion_queue',
  'dead_letter_queue',
  'delivery',
  'writer',
  'subscriptions',
  'ecs_events',
  'logs',
]);

const isInfrastructureComponent = (component: ObservabilityCollectionComponent): boolean => infrastructureComponentKeys.has(component.key);

const evidenceLabel = (component: ObservabilityCollectionComponent): string => {
  if (component.key === 'database') return 'Query passed';
  if (component.observations === undefined || component.observations === null) return 'Not measured';
  const count = formatInteger(component.observations);
  if (component.key === 'ingestion_queue') return `${count} pending`;
  if (component.key === 'writer') return `${count} mapping${component.observations === 1 ? '' : 's'}`;
  if (component.key === 'subscriptions') return `${count} configured`;
  if (component.key === 'ecs_events') return `${count} target${component.observations === 1 ? '' : 's'}`;
  if (component.key === 'logs') return `${count} log groups`;
  return count;
};

const freshnessLabel = (component: ObservabilityCollectionComponent, emptyLabel: string): string => {
  if (!component.lastReceivedAt) return emptyLabel;
  const prefix = isInfrastructureComponent(component) ? 'Checked' : 'Received';
  return `${prefix} ${formatRelativeTime(component.lastReceivedAt)}`;
};

const componentTone = (component: ObservabilityCollectionComponent): 'amber' | 'green' | 'slate' => {
  if (component.status === 'active') return 'green';
  if (component.status === 'missing') return 'amber';
  return 'slate';
};

const detailValue = (value: unknown): string => (typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value));

const retentionTone = (item: ObservabilityRetentionTableStatus): 'amber' | 'green' | 'slate' => {
  if (item.cleanupBlockedReason || !item.rollupCoverageComplete) return 'amber';
  if (item.enabled) return 'green';
  return 'slate';
};

const retentionState = (item: ObservabilityRetentionTableStatus): string => {
  if (!item.enabled) return 'Disabled';
  if (item.cleanupBlockedReason || !item.rollupCoverageComplete) return 'Backfill required';
  return 'Protected and eligible';
};

const ComponentDetail = ({ component }: { component: ObservabilityCollectionComponent }) => (
  <div className="grid gap-2">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-ink font-semibold">{component.label}</p>
        <p className="text-muted mt-1 text-xs">Owner: {component.owner}</p>
      </div>
      <Badge tone={componentTone(component)}>{component.status}</Badge>
    </div>
    <p className="text-muted text-sm">{component.summary}</p>
    <div className="text-muted flex flex-wrap gap-x-4 gap-y-1 text-xs">
      <span>{evidenceLabel(component)}</span>
      <span>{freshnessLabel(component, 'Freshness not measured')}</span>
    </div>
    {component.limitation ? <p className="text-muted border-line border-t pt-2 text-xs">{component.limitation}</p> : null}
  </div>
);

const collectionTone = (componentCount: number, missingCount: number, unknownCount: number): 'coverage' | 'healthy' | 'stale' => {
  if (componentCount === 0) return 'coverage';
  if (missingCount > 0) return 'coverage';
  if (unknownCount > 0) return 'stale';
  return 'healthy';
};

const collectionTitle = (componentCount: number, missingCount: number, unknownCount: number): string => {
  if (componentCount === 0) return 'Collection component evidence is unavailable';
  if (missingCount > 0) return `${formatInteger(missingCount)} collection signals have no observations in this scope`;
  if (unknownCount > 0) {
    return `Recorded signals are available; ${formatInteger(unknownCount)} infrastructure checks remain unmeasured`;
  }
  return 'Every governed collection component reports current evidence';
};

export const HealthPage = () => {
  const { state } = useRouteContext();
  const healthQuery = useEnvironmentHealthQuery(state);

  return (
    <DataPanel
      error={healthQuery.error}
      isError={healthQuery.isError}
      isLoading={healthQuery.isLoading}
      loadingLabel={`Loading ${state.env} collection health`}
      onRetry={() => void healthQuery.refetch()}
    >
      {(() => {
        const components = healthQuery.data?.components ?? [];
        const missing = components.filter((component) => component.status === 'missing');
        const unknown = components.filter((component) => component.status === 'unknown');
        const active = components.filter((component) => component.status === 'active');
        const retentionTables = healthQuery.data?.retentionTables ?? [];
        const tone = collectionTone(components.length, missing.length, unknown.length);

        return (
          <div className="grid min-w-0 gap-5">
            <StateView title={collectionTitle(components.length, missing.length, unknown.length)} tone={tone}>
              {components.length === 0
                ? 'The API returned no governed component checks. Product evidence below remains bounded and must not be interpreted as infrastructure health.'
                : `${formatInteger(active.length)} active · ${formatInteger(missing.length)} missing · ${formatInteger(unknown.length)} unknown. Generated ${formatTime(healthQuery.data?.generatedAt)}.`}
            </StateView>

            <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Card>
                <CardContent>
                  <div className="text-success flex items-center gap-2 text-xs font-semibold">
                    <Activity aria-hidden="true" className="size-4" /> Active
                  </div>
                  <p className="text-ink numeric mt-2 text-2xl font-semibold">{formatInteger(active.length)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <div className="text-warning flex items-center gap-2 text-xs font-semibold">
                    <TriangleAlert aria-hidden="true" className="size-4" /> Missing evidence
                  </div>
                  <p className="text-ink numeric mt-2 text-2xl font-semibold">{formatInteger(missing.length)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <div className="text-muted flex items-center gap-2 text-xs font-semibold">
                    <CircleHelp aria-hidden="true" className="size-4" /> Not measured here
                  </div>
                  <p className="text-ink numeric mt-2 text-2xl font-semibold">{formatInteger(unknown.length)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <div className="text-muted flex items-center gap-2 text-xs font-semibold">
                    <Database aria-hidden="true" className="size-4" /> Query scope
                  </div>
                  <p className="text-ink mt-2 text-sm font-semibold">{state.env}</p>
                  <p className="text-muted mt-1 text-xs">Bounded selected window</p>
                </CardContent>
              </Card>
            </section>

            <div className="grid gap-3 md:hidden">
              {components.map((component) => (
                <Card key={component.key}>
                  <CardContent>
                    <ComponentDetail component={component} />
                  </CardContent>
                </Card>
              ))}
            </div>

            <TableShell className="hidden md:block">
              <Table aria-label="Collection component health" className="table-fixed" style={{ minWidth: '62rem' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Component / owner</TableHead>
                    <TableHead className="w-28">State</TableHead>
                    <TableHead className="w-28">Evidence</TableHead>
                    <TableHead className="w-44">Freshness</TableHead>
                    <TableHead className="w-[28rem]">Interpretation / limitation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {components.map((component) => (
                    <TableRow key={component.key}>
                      <TableCell>
                        <p className="font-semibold">{component.label}</p>
                        <p className="text-muted mt-1 text-xs">{component.owner}</p>
                      </TableCell>
                      <TableCell>
                        <Badge tone={componentTone(component)}>{component.status}</Badge>
                      </TableCell>
                      <TableCell className="numeric">{evidenceLabel(component)}</TableCell>
                      <TableCell title={formatTime(component.lastReceivedAt)}>{freshnessLabel(component, 'Not measured')}</TableCell>
                      <TableCell>
                        <p>{component.summary}</p>
                        {Object.keys(component.details ?? {}).length > 0 ? (
                          <p className="text-muted mt-1 text-xs">
                            {Object.entries(component.details ?? {})
                              .map(([key, value]) => `${key}: ${detailValue(value)}`)
                              .join(' · ')}
                          </p>
                        ) : null}
                        {component.limitation ? <p className="text-muted mt-1 text-xs">{component.limitation}</p> : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableShell>

            <Card>
              <CardHeader>
                <CardTitle>Retention and rollup protection</CardTitle>
                <p className="text-muted text-sm">
                  Cleanup is blocked until every eligible source date has a preserved source-count marker and created-at watermark.
                  Preserved counts may exceed remaining raw rows after cleanup. Heap and index values are measured PostgreSQL allocation,
                  not estimates derived from row counts.
                </p>
              </CardHeader>
              <CardContent>
                {retentionTables.length === 0 ? (
                  <StateView title="No retention maintenance snapshot is available" tone="coverage">
                    Run {state.env} maintenance after deployment. Absence of a snapshot does not mean cleanup is safe or that tables use no
                    storage.
                  </StateView>
                ) : (
                  <TableShell>
                    <Table aria-label="Retention policy and rollup protection" style={{ minWidth: '72rem' }}>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Raw table</TableHead>
                          <TableHead>Policy</TableHead>
                          <TableHead>Cleanup state</TableHead>
                          <TableHead className="text-right">Eligible rows</TableHead>
                          <TableHead className="text-right">Covered days</TableHead>
                          <TableHead className="text-right">Heap</TableHead>
                          <TableHead className="text-right">Indexes</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {retentionTables.map((item) => (
                          <TableRow key={item.tableName}>
                            <TableCell>
                              <p className="font-mono text-xs font-semibold">{item.tableName}</p>
                              <p className="text-muted mt-1 text-xs" title={formatTime(item.measuredAt)}>
                                Measured {formatRelativeTime(item.measuredAt)}
                              </p>
                            </TableCell>
                            <TableCell>{formatInteger(item.retentionDays)} days</TableCell>
                            <TableCell>
                              <Badge tone={retentionTone(item)}>{retentionState(item)}</Badge>
                              {item.cleanupBlockedReason ? <p className="text-muted mt-1 text-xs">{item.cleanupBlockedReason}</p> : null}
                            </TableCell>
                            <TableCell className="numeric text-right">
                              {formatInteger(item.remainingEligibleRows)}
                              {(item.deletedRows ?? 0) > 0 ? (
                                <p className="text-muted text-xs">{formatInteger(item.deletedRows)} deleted in batch</p>
                              ) : null}
                            </TableCell>
                            <TableCell className="numeric text-right">
                              {formatInteger(item.coveredSourceDays)} / {formatInteger(item.eligibleSourceDays)}
                              {(item.missingRollupDays ?? 0) > 0 ? (
                                <p className="text-warning text-xs">{formatInteger(item.missingRollupDays)} missing</p>
                              ) : null}
                            </TableCell>
                            <TableCell className="numeric text-right">{formatBytes(item.heapBytes)}</TableCell>
                            <TableCell className="numeric text-right">{formatBytes(item.indexBytes)}</TableCell>
                            <TableCell className="numeric text-right font-semibold">{formatBytes(item.totalBytes)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableShell>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex items-center gap-2">
                <RadioTower aria-hidden="true" className="text-muted size-4" />
                <CardTitle>Observed product evidence</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
                {[
                  ['Runs', healthQuery.data?.totalRuns],
                  ['Failed runs', healthQuery.data?.failedRuns],
                  ['Open failures', healthQuery.data?.openFailures],
                  ['Unreviewed', healthQuery.data?.unconsumedFailures],
                  ['Client errors', healthQuery.data?.clientErrors],
                  ['RUM sessions', healthQuery.data?.rumSessions],
                ].map(([label, value]) => (
                  <div className="border-line rounded-md border p-3" key={String(label)}>
                    <p className="text-muted text-xs font-semibold">{label}</p>
                    <p className="text-ink numeric mt-2 text-lg font-semibold">{formatInteger(Number(value ?? 0))}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        );
      })()}
    </DataPanel>
  );
};
